import { IRegisterCredentials, IAuthResponse, IPasswordEntry, IDecryptedPasswordEntry, ICreatePasswordEntry } from '../types';
import { OrganizationSignup } from '../types/license.types';
import { decryptData, deriveKeys, encryptData, encryptKeyData, encryptVaultKey, decryptVaultKey, decryptKeyData } from '../utils/encryption';
import { secureStore } from '../utils/secureStore';
import { store } from '../store';
import { handleSessionExpired } from './sessionHandler';
import { ProfileData } from '../types/auth.types';
import { setCredentials } from '../store/slices/authSlice';

import { config } from '../config';
const API_URL = config.API_URL;

interface ILoginRequest {
    email: string;
    authKey: string;
    isFromExtension?: boolean;
}

export const AuthService = {
    async checkSession(): Promise<IAuthResponse> {
        try {
            // Try to get session token from cookie automatically (browser will send it)
            const response = await fetch(`${API_URL}/users/session-login`, {
                method: 'POST',
                credentials: 'include', // Send cookies for session auth
            });
            const data = await response.json();
            return data;
        } catch (error) {
            throw new Error('Session check failed');
        }
    },
    async login(credentials: ILoginRequest): Promise<IAuthResponse> {
        try {
            const response = await fetch(`${API_URL}/users/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials),
                credentials: 'include', // Send cookies for session auth
            });

            // Don't call handleSessionExpired here - 401 on login means wrong credentials, not session expiry
            const data = await response.json();
            return data;
        } catch (error) {
            throw new Error('Login failed');
        }
    },

    async register(credentials: IRegisterCredentials): Promise<IAuthResponse> {
        try {
            // Derive keys on client side
            const { authKey, encryptionKey, symmetricKey } = deriveKeys(
                credentials.password,
                credentials.email
            );
            // Encrypt symmetric key with encryption key
            const encryptedVaultKey = encryptVaultKey(symmetricKey, encryptionKey);
            
            const encryptedKey = encryptKeyData(encryptionKey); // Use your encryption function
            // Send only derived auth key to server, never the master password
            const response = await fetch(`${API_URL}/users/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: credentials.email,
                    authKey: authKey,
                    encryptedVaultKey: encryptedVaultKey,
                    encryptedKey: encryptedKey,
                    masterPasswordHint: credentials.masterPasswordHint,
                    is_invited: credentials.is_invited
                }),
                credentials: 'include', // Send cookies for session auth
            });

            const data = await response.json();
            return data;
        } catch (error) {
            throw new Error('Registration failed');
        }
    },

    async logout(): Promise<void> {
        try {
            // Call backend to clear session cookie
            await fetch(`${API_URL}/users/logout`, {
                method: 'POST',
                credentials: 'include', // Ensure cookies are sent
            });
        } catch (error) {
            console.error('Logout API error:', error);
        }
        
        // Notify extension about logout (if in web context)
        if (typeof window !== 'undefined' && window.postMessage) {
            window.postMessage({
                type: 'WEB_LOGOUT_SUCCESS',
                payload: {}
            }, window.location.origin);
            console.log('Web: Logout message posted for extension sync');
        }
        
        // Clear workspace-related data from localStorage
        try {
            localStorage.removeItem('activeWorkspace');
            localStorage.removeItem('workspaces');
            const persistedData = localStorage.getItem('persist:root');
            if (persistedData) {
                const parsedData = JSON.parse(persistedData);
                if (parsedData.workspace) {
                    const workspaceState = {
                        workspaces: '[]',
                        activeWorkspaceId: 'null',
                        activeWorkspace: 'null',
                        loading: 'false',
                        error: 'null'
                    };
                    parsedData.workspace = JSON.stringify(workspaceState);
                    localStorage.setItem('persist:root', JSON.stringify(parsedData));
                }
            }
        } catch (error) {
            console.error('Error clearing workspace data:', error);
        }
    },
    encryptEntry(entry: Partial<ICreatePasswordEntry>, vaultKey: string): any {
        
        try {
            if (!entry.title || !entry.username || !entry.password) {
                throw new Error('Missing required fields');
            }

            let encryptedUsername, encryptedPassword, encryptedNotes;
            
            try {
                encryptedUsername = encryptData(entry.username, vaultKey);
            } catch (error) {
                console.error('Username encryption failed:', error);
                throw new Error('Failed to encrypt username');
            }

            try {
                encryptedPassword = encryptData(entry.password, vaultKey);
            } catch (error) {
                console.error('Password encryption failed:', error);
                throw new Error('Failed to encrypt password');
            }

            if (entry.notes) {
                try {
                    encryptedNotes = encryptData(entry.notes, vaultKey);
                } catch (error) {
                    console.error('Notes encryption failed:', error);
                    encryptedNotes = null;
                }
            }

            return {
                title: entry.title,
                encrypted_username: encryptedUsername,
                encrypted_password: encryptedPassword,
                encrypted_notes: encryptedNotes,
                website_url: entry.website_url || '',
                category_id: entry.category_id || '',
                favourite: entry.favourite || false
            };
        } catch (error) {
            console.error('Encryption error:', error);
            throw new Error('Failed to encrypt password entry');
        }
    },
    async decryptEntry(entry: IPasswordEntry, vaultKey: string): Promise<IDecryptedPasswordEntry> {
        try {
            // Helper function to safely decrypt buffer data
            const decryptBufferData = (encryptedBuffer: any): string => {
                if (!encryptedBuffer) return '';

                try {
                    // Handle buffer data from server
                    if (encryptedBuffer.data && Array.isArray(encryptedBuffer.data)) {
                        // Convert array back to encrypted string
                        const uint8Array = new Uint8Array(encryptedBuffer.data);
                        const encryptedString = new TextDecoder().decode(uint8Array);
                        
                        // Decrypt the data
                        return decryptData(encryptedString, vaultKey);
                    }
                    
                    // If it's already a string
                    if (typeof encryptedBuffer === 'string') {
                        return decryptData(encryptedBuffer, vaultKey);
                    }

                    return '';
                } catch (error) {
                    console.error('Error decrypting buffer:', error, {
                        bufferType: typeof encryptedBuffer,
                        hasData: !!encryptedBuffer?.data
                    });
                    return ''; // Return empty string instead of throwing
                }
            };


            // Decode encrypted data with error handling for each field
            const decryptedData = {
                id: entry.id,
                vault_id: entry.vault_id,
                title: entry.title,
                username: decryptBufferData(entry.encrypted_username),
                password: decryptBufferData(entry.encrypted_password),
                notes: entry.encrypted_notes ? 
                    decryptBufferData(entry.encrypted_notes) : undefined,
                website_url: entry.website_url || '',
                category_id: entry.category_id || '',
                favourite: entry.favourite,
                last_used: entry.last_used ? new Date(entry.last_used) : undefined,
                password_strength: entry.password_strength || '',
                created_at: new Date(entry.created_at),
                updated_at: new Date(entry.updated_at)
            };


            // Validate decrypted data
            if (!decryptedData.username || !decryptedData.password) {
                throw new Error('Failed to decrypt critical fields');
            }

            return decryptedData;
        } catch (error) {
            console.error('Error processing entry:', error);
            // Return a partially decrypted entry instead of throwing
            return {
                id: entry.id,
                vault_id: entry.vault_id,
                title: entry.title,
                username: '(Decryption failed)',
                password: '(Decryption failed)',
                notes: undefined,
                website_url: entry.website_url || '',
                category_id: entry.category_id || '',
                favourite: entry.favourite,
                last_used: entry.last_used ? new Date(entry.last_used) : undefined,
                password_strength: entry.password_strength || '',
                created_at: new Date(entry.created_at),
                updated_at: new Date(entry.updated_at)
            };
        }
    },

     async forgotPassword(email: string): Promise<any> {
        try {

            const response = await fetch(`${API_URL}/users/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();
            return data;
        } catch (error) {
            throw error;
        }
    },

    async getPasswordHint(email: string): Promise<any> {
        try {
            const response = await fetch(`${API_URL}/users/password-hint`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();
            return data;
        } catch (error) {
            throw error;
        }
    },

    async requestPasswordReset(email: string): Promise<any> {
        try {
            const response = await fetch(`${API_URL}/users/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async verifyRecoveryToken(email: string, token: string): Promise<any> {
        try {
            const response = await fetch(`${API_URL}/users/verify-recovery`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, token }),
            });
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async resetPassword(tempToken: string, newPassword: string, email: string, encryptedVaultKey: string, oldEncryptionKey: string, cryptoVersion?: number, rsaEncryptedVaultKey?: string, publicKey?: string, oldEncryptedPrivateKey?: string): Promise<any> {
        try {
            console.log('Starting password reset process...');
            console.log(`Crypto version: ${cryptoVersion || 1}`);
            
            const userCryptoVersion = cryptoVersion || 1;

            // STEP 1: Decrypt the old vault key and private key (for v2) based on crypto version
            let oldSymmetricKey: string;
            let oldPrivateKey: CryptoKey | null = null;

            if (userCryptoVersion === 1) {
                // Legacy encryption - decrypt with encryption key
                console.log('Decrypting vault key for crypto_version 1');
                oldSymmetricKey = decryptVaultKey(encryptedVaultKey, oldEncryptionKey);
            } else if (userCryptoVersion === 2) {
                // RSA encryption - decrypt private key, then decrypt vault key
                console.log('Decrypting vault key for crypto_version 2 (RSA)');
                
                if (!rsaEncryptedVaultKey || !publicKey || !oldEncryptedPrivateKey) {
                    throw new Error('Missing RSA keys for crypto_version 2 user');
                }

                // Import RSA utilities
                const { hybridDecrypt, loadPrivateKey } = await import('../utils/rsaCrypto');

                // Parse the encrypted private key if it's a string, otherwise use as-is
                let encryptedPrivateKeyObj;
                if (typeof oldEncryptedPrivateKey === 'string') {
                    try {
                        encryptedPrivateKeyObj = JSON.parse(oldEncryptedPrivateKey);
                    } catch (e) {
                        throw new Error('Invalid encryptedPrivateKey format');
                    }
                } else {
                    encryptedPrivateKeyObj = oldEncryptedPrivateKey;
                }
                
                // Load and decrypt the private key (handles PEM conversion internally)
                oldPrivateKey = await loadPrivateKey(encryptedPrivateKeyObj, oldEncryptionKey);
                
                // Parse the RSA encrypted vault key if it's a string, otherwise use as-is
                let rsaVaultKeyObj;
                if (typeof rsaEncryptedVaultKey === 'string') {
                    try {
                        rsaVaultKeyObj = JSON.parse(rsaEncryptedVaultKey);
                    } catch (e) {
                        throw new Error('Invalid rsaEncryptedVaultKey format');
                    }
                } else {
                    rsaVaultKeyObj = rsaEncryptedVaultKey;
                }
                
                // Decrypt the vault key with private key (RSA hybrid decryption)
                oldSymmetricKey = await hybridDecrypt(
                    rsaVaultKeyObj.encryptedData,
                    rsaVaultKeyObj.encryptedKey,
                    rsaVaultKeyObj.iv,
                    oldPrivateKey,
                    rsaVaultKeyObj.authTag
                );
            } else {
                throw new Error(`Unsupported crypto version: ${userCryptoVersion}`);
            }

            console.log('Old vault key decrypted successfully');

            // STEP 1.5: For v2 users, fetch ALL V2 vault keys (personal + shared vaults) and prepare for re-encryption
            let allVaultKeys: any[] = [];
            if (userCryptoVersion === 2 && oldPrivateKey) {
                try {
                    console.log('Fetching all V2 vault keys for v2 user...');
                    const vaultKeysResponse = await fetch(`${API_URL}/vault/vault-keys`, {
                        headers: {
                            'Authorization': `Bearer ${tempToken}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (vaultKeysResponse.ok) {
                        const vaultKeysData = await vaultKeysResponse.json();
                        if (vaultKeysData.success && vaultKeysData.vaultKeys) {
                            // ONLY process V2 vault keys (key_version === 2)
                            allVaultKeys = vaultKeysData.vaultKeys.filter((vk: any) => vk.cryptoVersion === 2);
                            console.log(`Fetched ${allVaultKeys.length} V2 vault keys for re-encryption (filtered from ${vaultKeysData.vaultKeys.length} total)`);
                        }
                    } else {
                        console.warn('Failed to fetch vault keys, will only update personal vault');
                    }
                } catch (error) {
                    console.error('Error fetching vault keys:', error);
                    console.warn('Will only update personal vault key');
                }
            }

            // STEP 2: Get all entries using temp token
            const response = await fetch(`${API_URL}/vault/entries-for-reset`, {
                headers: {
                    'Authorization': `Bearer ${tempToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch entries');
            }

            const entries = await response.json();
            console.log(`Fetched ${entries.length} entries for re-encryption`);

            // STEP 3: Generate new keys for the new password
            const { authKey, encryptionKey: newEncryptionKey, symmetricKey: newSymmetricKey } = deriveKeys(newPassword, email);
            console.log('New keys generated successfully');
            
            // CRITICAL FIX: For V2 users, we should NOT use the newSymmetricKey from deriveKeys
            // V2 vault keys are random and stored encrypted, not derived from password
            // We should reuse the OLD vault key and just re-encrypt it with the new public key
            const vaultKeyToUse = userCryptoVersion === 2 ? oldSymmetricKey : newSymmetricKey;
            console.log(`Using vault key: ${userCryptoVersion === 2 ? 'EXISTING (V2)' : 'NEW (V1)'}`);

            // STEP 4: Prepare new encryption keys based on crypto version
            let newEncryptedVaultKey: string | undefined;
            let newPublicKey: string | undefined;
            let newEncryptedPrivateKey: string | undefined;
            let newRsaEncryptedVaultKey: string | undefined;
            let reEncryptedVaultKeys: any[] = [];

            if (userCryptoVersion === 1) {
                // Legacy encryption - encrypt vault key with encryption key
                newEncryptedVaultKey = encryptVaultKey(vaultKeyToUse, newEncryptionKey);
                console.log('Generated new encrypted vault key for crypto_version 1');

            } else if (userCryptoVersion === 2) {
                // RSA encryption - generate new RSA key pair
                console.log('Generating new RSA key pair for crypto_version 2');
                
                const { 
                    generateRSAKeyPair, 
                    exportPublicKey, 
                    exportPrivateKey, 
                    hybridEncrypt,
                    hybridDecrypt,
                    encryptPrivateKey
                } = await import('../utils/rsaCrypto');

                // Generate new RSA key pair
                const keyPair = await generateRSAKeyPair();
                newPublicKey = await exportPublicKey(keyPair.publicKey);
                const privateKeyData = await exportPrivateKey(keyPair.privateKey);

                // Encrypt the private key with new encryption key
                const encryptedPrivateKeyObj = await encryptPrivateKey(privateKeyData, newEncryptionKey);
                newEncryptedPrivateKey = JSON.stringify(encryptedPrivateKeyObj);

                // CRITICAL: Encrypt the OLD vault key (not new) with new public key
                // This preserves all existing encrypted data
                const rsaEncryptedObj = await hybridEncrypt(vaultKeyToUse, keyPair.publicKey);
                newRsaEncryptedVaultKey = JSON.stringify(rsaEncryptedObj);

                console.log('New RSA keys generated and EXISTING vault key re-encrypted');

                // CRITICAL: Re-encrypt ALL V2 vault keys (personal + shared) with new public key
                // V1 vaults are left untouched
                if (allVaultKeys.length > 0 && oldPrivateKey) {
                    console.log(`Re-encrypting ${allVaultKeys.length} V2 vault keys with new public key...`);
                    
                    for (const vaultKey of allVaultKeys) {
                        try {
                            // Decrypt the vault key with old private key
                            const decryptedVaultKey = await hybridDecrypt(
                                vaultKey.encryptedData,
                                vaultKey.encryptedKey,
                                vaultKey.iv,
                                oldPrivateKey,
                                vaultKey.authTag
                            );

                            // Re-encrypt with new public key
                            const reEncryptedObj = await hybridEncrypt(decryptedVaultKey, keyPair.publicKey);

                            reEncryptedVaultKeys.push({
                                vaultId: vaultKey.vaultId,
                                encryptedData: reEncryptedObj.encryptedData,
                                encryptedKey: reEncryptedObj.encryptedKey,
                                iv: reEncryptedObj.iv,
                                authTag: reEncryptedObj.authTag
                            });

                            console.log(`✓ Re-encrypted V2 vault key for vault ${vaultKey.vaultId}`);
                        } catch (error) {
                            console.error(`Failed to re-encrypt vault key for vault ${vaultKey.vaultId}:`, error);
                            throw new Error(`Failed to re-encrypt vault key for vault ${vaultKey.vaultId}: ${error}`);
                        }
                    }

                    console.log(`Successfully re-encrypted ${reEncryptedVaultKeys.length} V2 vault keys`);
                } else if (userCryptoVersion === 2) {
                    console.log('⚠️ No V2 vault keys found to re-encrypt - will only update personal vault');
                }
                
                // NOTE: ORG private key re-encryption is now handled by the backend
                // The backend will decrypt ORG keys with the old private key and re-encrypt with new public key
                // This is more reliable as it uses Node.js crypto instead of Web Crypto API
            }

            // STEP 5: Re-encrypt password entries
            // For V2 users: Items DON'T need re-encryption (vault key unchanged, just return empty array)
            // For V1 users: Re-encrypt all entries (vault key changed, must re-encrypt everything)
            let reEncryptedEntries: any[] = [];
            
            if (userCryptoVersion === 1) {
                console.log(`Re-encrypting ${entries.length} password entries for V1 user...`);
                reEncryptedEntries = entries.length > 0 ? entries.map((entry: IPasswordEntry) => {
                    try {
                        // Convert Buffer data to string
                        const username = entry.encrypted_username.data ? 
                            new TextDecoder().decode(new Uint8Array(entry.encrypted_username.data)) : '';
                        const password = entry.encrypted_password.data ? 
                            new TextDecoder().decode(new Uint8Array(entry.encrypted_password.data)) : '';
                        const notes = entry.encrypted_notes ? 
                            new TextDecoder().decode(new Uint8Array(entry.encrypted_notes.data)) : null;

                        let decryptionKey = oldSymmetricKey;
                        let isSharedPassword = false;
                        
                        if (entry.shared_key) {
                            // V1: For shared passwords, use the shared key to decrypt the data
                            isSharedPassword = true;
                            try {
                                let sharedKeyStr: string;
                                if (typeof entry.shared_key === 'string') {
                                    sharedKeyStr = entry.shared_key;
                                } else if (entry.shared_key && typeof entry.shared_key === 'object' && 'data' in entry.shared_key) {
                                    sharedKeyStr = new TextDecoder().decode(new Uint8Array((entry.shared_key as any).data));
                                } else {
                                    throw new Error('Invalid shared_key format');
                                }
                                
                                decryptionKey = decryptKeyData(sharedKeyStr);
                                console.log(`Using shared key for entry ${entry.id}`);
                            } catch (error) {
                                console.error(`Failed to decrypt shared_key for entry ${entry.id}, will use personal key:`, error);
                                decryptionKey = oldSymmetricKey;
                            }
                        }

                        // Decrypt with the appropriate key
                        const decryptedUsername = decryptData(username, decryptionKey);
                        const decryptedPassword = decryptData(password, decryptionKey);
                        const decryptedNotes = notes ? decryptData(notes, decryptionKey) : null;

                        // Re-encrypt with the NEW vault key (V1 only)
                        const reEncryptedEntry: any = {
                            id: entry.id,
                            encrypted_username: encryptData(decryptedUsername, vaultKeyToUse),
                            encrypted_password: encryptData(decryptedPassword, vaultKeyToUse),
                            encrypted_notes: decryptedNotes ? 
                                encryptData(decryptedNotes, vaultKeyToUse) : null,
                            vault_id: entry.vault_id
                        };

                        // If this password is shared (v1), re-encrypt the shared_key with new vault key
                        if (isSharedPassword) {
                            try {
                                reEncryptedEntry.shared_key = encryptKeyData(vaultKeyToUse);
                                console.log(`Re-encrypted shared_key for entry ${entry.id}`);
                            } catch (error) {
                                console.error(`Failed to re-encrypt shared_key for entry ${entry.id}:`, error);
                                throw error;
                            }
                        }

                        return reEncryptedEntry;
                    } catch (error) {
                        console.error(`Failed to process entry ${entry.id}:`, error);
                        throw new Error(`Failed to process entry ${entry.id}: ${error}`);
                    }
                }) : [];
                console.log(`Successfully re-encrypted ${reEncryptedEntries.length} V1 entries`);
            } else {
                // V2: Vault key unchanged, items don't need re-encryption
                console.log(`V2 user: Skipping item re-encryption (vault key unchanged, only RSA keys rotated)`);
                reEncryptedEntries = [];
            }

            // STEP 6: Send reset request to backend
            const encryptedKey = encryptKeyData(newEncryptionKey);
            const oldEncryptedKey = encryptKeyData(oldEncryptionKey);

            const requestBody: any = {
                authKey,
                reEncryptedEntries,
                email,
                encryptionKey: encryptedKey,
                oldEncryptionKey: oldEncryptedKey // Send old encryption key for ORG key re-encryption
            };

            // Add version-specific fields
            if (userCryptoVersion === 1) {
                requestBody.encryptedVaultKey = newEncryptedVaultKey;
            } else if (userCryptoVersion === 2) {
                requestBody.publicKey = newPublicKey;
                requestBody.encryptedPrivateKey = newEncryptedPrivateKey;
                requestBody.rsaEncryptedVaultKey = newRsaEncryptedVaultKey;
                // CRITICAL: Include re-encrypted vault keys for ALL vaults (personal + shared)
                if (reEncryptedVaultKeys.length > 0) {
                    requestBody.reEncryptedVaultKeys = reEncryptedVaultKeys;
                    console.log(`Sending ${reEncryptedVaultKeys.length} re-encrypted vault keys to backend`);
                }
                // NOTE: ORG key re-encryption is now handled entirely by backend
            }

            const resetResponse = await fetch(`${API_URL}/users/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tempToken}`
                },
                body: JSON.stringify(requestBody)
            });

            if (!resetResponse.ok) {
                const errorData = await resetResponse.json();
                throw new Error(errorData.message || 'Password reset failed');
            }

            const data = await resetResponse.json();
            console.log('Password reset completed successfully');
            
            if (data.success) {
                // Store the correct vault key (OLD for V2, NEW for V1)
                secureStore.setKeys(newEncryptionKey, vaultKeyToUse, vaultKeyToUse);
                console.log(`Stored keys - Vault key: ${userCryptoVersion === 2 ? 'PRESERVED (V2)' : 'ROTATED (V1)'}`);
            }

            return data;
        } catch (error) {
            console.error('Reset password error:', error);
            throw error;
        }
    },

    async verifyMfa(data: { code: string; tempToken: string }): Promise<IAuthResponse> {
        try {
            console.log("verifyMfa", data);
            const response = await fetch(`${API_URL}/users/verify-mfa`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
                credentials: 'include', // CRITICAL: Include cookies for session auth
            });
            console.log("verifyMfa response", response);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'MFA verification failed');
            }

            const responseData = await response.json();
            console.log("verifyMfa responseData", responseData);
            return responseData;
        } catch (error) {
            console.error('MFA verification error:', error);
            throw error;
        }
    },

    async removeMFA(userId: string): Promise<any> {
        try {
            const response = await fetch(`${API_URL}/users/2fa/remove`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userId }),
                credentials: 'include', // Send cookies for session auth
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to remove MFA');
            }
            
            return await response.json();
        } catch (error) {
            console.error('Remove MFA error:', error);
            throw error;
        }
    },

    async requestMfaHelp(email: string, message: string): Promise<any> {
        try {
            const response = await fetch(`${API_URL}/users/mfa-help-request`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, message }),
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to send MFA help request');
            }
            
            return await response.json();
        } catch (error) {
            console.error('MFA help request error:', error);
            throw error;
        }
    },

    async getUserProfiles(): Promise<ProfileData[]> {
        try {
            console.log('AuthService.getUserProfiles() called');
            const response = await fetch(`${API_URL}/users/profiles`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            // Check for 401 status - session expired
            if (response.status === 401) {
                handleSessionExpired();
                throw new Error('Session expired');
            }

            const data = await response.json();
            
            if (!data.success) throw new Error(data.message || 'Failed to fetch profiles');
            // Ensure we transform the API response to match the ProfileData type
            return data.profiles.map((profile: any) => ({
                user_id: profile.user_id,
                email: profile.email,
                is_active: profile.is_active,
                is_current_user: profile.is_current_user,
                workspaces: Array.isArray(profile.workspaces) 
                    ? profile.workspaces.map((workspace: any) => ({
                        id: workspace.id,
                        name: workspace.name,
                        team_type: workspace.team_type,
                        role_id: workspace.role_id,
                        role_name: workspace.role_name,
                        is_personal: workspace.is_personal,
                        is_active: workspace.is_active
                      }))
                    : []
            }));
        } catch (error) {
            console.error('Error fetching profiles:', error);
            throw error;
        }
    },

    async setActiveWorkspace(workspaceId: string): Promise<any> {
        try {
            const response = await fetch(`${API_URL}/users/active-workspace`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ workspaceId }),
                credentials: 'include',
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error setting active workspace:', error);
            throw error;
        }
    },

    async initiateProfileLink(email: string): Promise<{success: boolean; message: string}> {
        try {
            const response = await fetch(`${API_URL}/users/initiate-profile-link`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
                credentials: 'include',
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error initiating profile link:', error);
            throw error;
        }
    },

    async verifyProfileEmail(email: string, code: string): Promise<{success: boolean; message: string; existingUser: boolean; existingUserId: string | null}> {
        try {
            const response = await fetch(`${API_URL}/users/verify-profile-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, code }),
                credentials: 'include',
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error verifying email:', error);
            throw error;
        }
    },

    async registerLinkedProfile(
        email: string,
        authKey: string, 
        encryptedVaultKey: string, 
        encryptedKey: string,
        masterPasswordHint: string | null,
        linkToUserId: string,
    ): Promise<{success: boolean; data: any}> {
        try {
            const response = await fetch(`${API_URL}/users/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    authKey,
                    encryptedVaultKey,
                    encryptedKey,
                    masterPasswordHint,
                    linkToUserId,
                }),
                credentials: 'include',
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error registering profile:', error);
            throw error;
        }
    },

    async switchProfile(profileId: string): Promise<{success: boolean; data: any}> {
        try {
            const response = await fetch(`${API_URL}/users/switch-profile/${profileId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();
            if (!data.success) throw new Error(data.message || 'Failed to switch profiles');
            // Return the data but don't update localStorage or dispatch actions here
            // Let the component handle that for better control of the UI flow
            return data;
        } catch (error) {
            console.error('Error switching profile:', error);
            throw error;
        }
    },

    async registerOrganization(data: OrganizationSignup): Promise<IAuthResponse> {
        try {
            const response = await fetch(`${API_URL}/users/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });

            const responseData = await response.json();
            return responseData;
        } catch (error) {
            throw new Error('Organization registration failed');
        }
    },

    async checkUserExists(email: string): Promise<{ exists: boolean }> {
        try {
            const response = await fetch(`${API_URL}/users/check-exists`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error checking user existence:', error);
            throw error;
        }
    },

    async updateSecretKey(encryptedVaultKey: string): Promise<{ success: boolean; message?: string }> {
        try {
            const response = await fetch(`${API_URL}/users/update-secret-key`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ vaultKey: encryptedVaultKey }),
                credentials: 'include', // Send cookies for session auth
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update secret key');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error updating secret key:', error);
            throw new Error('Failed to update secret key');
        }
    },

    // Unlock account using recovery token
    async unlockAccount(token: string, email: string): Promise<{ success: boolean; message: string }> {
        try {
            const response = await fetch(`${API_URL}/users/unlock-account`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, email }),
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to unlock account');
            }

            return data;
        } catch (error) {
            console.error('Error unlocking account:', error);
            throw error;
        }
    },

    // Allow user to self-lock their account
    async lockMyAccount(email: string): Promise<{ success: boolean; message: string }> {
        try {
            const response = await fetch(`${API_URL}/users/lock-my-account`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to lock account');
            }

            return data;
        } catch (error) {
            console.error('Error locking account:', error);
            throw error;
        }
    },

    // Admin unlock account - for administrators to unlock other users' accounts
    async adminUnlockAccount(email: string, userId: string): Promise<{ success: boolean; message: string }> {
        try {
            const response = await fetch(`${API_URL}/users/admin-unlock-account`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, userId }),
                credentials: 'include', // Send cookies for session auth
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to unlock account');
            }

            return data;
        } catch (error) {
            console.error('Error unlocking account:', error);
            throw error;
        }
    },

    // Migrate user to RSA encryption - SIMPLIFIED (only generates keys)
    async migrateToRSA(vaultKey: string, encryptionKey: string): Promise<{ success: boolean; message: string; cryptoVersion?: number }> {
        try {
            console.log('🔒 Migration: Starting V2 key generation...');

            // Send both vaultKey (for temp storage) and encryptionKey (for RSA private key encryption)
            const response = await fetch(`${API_URL}/users/migrate-to-rsa`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    vaultKey,
                    encryptionKey
                }),
                credentials: 'include',
            });

            const data = await response.json();
            
            console.log('📡 Migration: Backend response:', data);
            
            if (!response.ok) {
                console.error('❌ Migration failed:', data.message);
                throw new Error(data.message || 'Migration failed');
            }            console.log('✅ RSA keys generated successfully!');
            
            return data;
        } catch (error: any) {
            console.error('❌ RSA migration error:', error);
            throw new Error(error.message || 'Failed to migrate to RSA encryption');
        }
    },

    async changeMasterPassword(currentPassword: string, newPassword: string, email: string): Promise<any> {
        try {
            // Derive keys from current password
            const { authKey: currentAuthKey, encryptionKey: currentEncryptionKey } = deriveKeys(currentPassword, email);
            
            // Derive keys from new password
            const { authKey: newAuthKey, encryptionKey: newEncryptionKey, symmetricKey: newSymmetricKey } = deriveKeys(newPassword, email);
            
            // Encrypt new vault key with new encryption key
            const newEncryptedVaultKey = encryptVaultKey(newSymmetricKey, newEncryptionKey);
            
            const response = await fetch(`${API_URL}/users/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    currentAuthKey,
                    newAuthKey,
                    currentEncryptionKey,
                    newEncryptionKey,
                    newEncryptedVaultKey
                }),
                credentials: 'include',
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Password change failed');
            }

            return data;
        } catch (error: any) {
            console.error('Change password error:', error);
            throw new Error(error.message || 'Failed to change master password');
        }
    },

    async getUserKeys(): Promise<{ success: boolean; cryptoVersion: number; publicKey?: string; encryptedPrivateKey?: any; vaultKeys?: any }> {
        try {
            const response = await fetch(`${API_URL}/users/my-keys`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to fetch user keys');
            }
            return data;
        } catch (error: any) {
            console.error('❌ Get user keys error:', error);
            throw new Error(error.message || 'Failed to fetch user keys');
        }
    },

    async resetRSAKeys(publicKey: string, encryptedPrivateKey: any, vaultKeys: any): Promise<{ success: boolean; message: string }> {
        try {
            console.log('🔒 Healing: Resetting RSA keys...');
            const response = await fetch(`${API_URL}/users/reset-rsa-keys`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    publicKey,
                    encryptedPrivateKey,
                    vaultKeys
                }),
                credentials: 'include',
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to reset RSA keys');
            }
            return data;
        } catch (error: any) {
            console.error('❌ Reset RSA keys error:', error);
            throw new Error(error.message || 'Failed to reset RSA keys');
        }
    },
};

